<template>
  <div class="h-screen bg-gray-50 flex flex-col">
    <!-- 筛选和搜索 -->
    <div class="flex-shrink-0 bg-white p-2 mb-1 shadow-sm">
      <!-- 统计卡片 -->
      <div class="flex items-center justify-between mb-2">
        <div class="grid grid-cols-3 gap-2 flex-1">
          <div class="text-center">
            <p class="text-lg font-bold text-green-600">{{ summaryData.totalAmount.toFixed(2) }}</p>
            <p class="text-xs text-gray-500">销售金额</p>
          </div>
          <div class="text-center">
            <p class="text-lg font-bold text-blue-600">¥{{ summaryData.orderCount }}</p>
            <p class="text-xs text-gray-500">销售量</p>
          </div>
          <button
            type="button"
            @click="handleUnauditedStatClick"
            :class="[
              'text-center rounded-lg py-1 transition-colors active:scale-95',
              filters.auditStatus === 0 ? 'bg-orange-50 ring-1 ring-orange-200' : 'hover:bg-orange-50'
            ]"
          >
            <p class="text-lg font-bold text-orange-600">{{ summaryData.unAuditOrder }}</p>
            <p class="text-xs text-gray-500">未审核</p>
          </button>
        </div>
        <!-- 快捷操作菜单按钮 -->
        <div class="relative ml-2">
          <button @click="showQuickActions = !showQuickActions"
            class="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
          <!-- 快捷操作弹出菜单 -->
          <div v-if="showQuickActions" 
            class="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 min-w-[120px]">
            <button @click="handleQuickAction('/mobile/sales/add')"
              class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 rounded-t-lg">
              <svg class="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>新增销售</span>
            </button>
            <button @click="handleQuickAction('/mobile/sales/return-list')"
              class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 border-t border-gray-100">
              <svg class="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3" />
              </svg>
              <span>销售退货</span>
            </button>
            <button @click="handleQuickAction('/mobile/sales/customers')"
              class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 rounded-b-lg">
              <svg class="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span>客户管理</span>
            </button>
          </div>
        </div>
      </div>

      <div class="flex items-center space-x-3">
        <div class="flex-1">
          <input v-model="searchKeyword" type="text" placeholder="搜索销售单号、客户、商品名称..."
            class="w-full px-3 py-2 border-0 bg-gray-50 rounded-lg focus:ring-2 focus:ring-green-500 focus:bg-white focus:outline-none"
            @input="debounceSearch" />
        </div>
        <button @click="handleSearch" :disabled="searching"
          class="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50">
          <svg v-if="searching" class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor"
              d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
            </path>
          </svg>
          <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="m21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>
        <button @click="showFilter = !showFilter"
          class="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
        </button>
      </div>

      <!-- 筛选选项 -->
      <div v-if="showFilter" class="border-gray-200 pt-3 space-y-3">
        <div class="grid grid-cols-2 gap-3">
          <select v-model="filters.auditStatus" class="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            @change="applyFilters">
            <option :value="null">仓库审核状态</option>
            <option :value="0">未审核</option>
            <option :value="1">已审核</option>
          </select>

          <select v-model="filters.salesmanAuditStatus" class="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            @change="applyFilters">
            <option :value="null">业务员审核状态</option>
            <option :value="0">未审核</option>
            <option :value="1">已审核</option>
          </select>

          <select v-model="filters.paymentStatus" class="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            @change="applyFilters">
            <option value="">全部收款状态</option>
            <option :value="0">未收款</option>
            <option :value="1">部分收款</option>
            <option :value="2">已收款</option>
            <option :value="3">已核销</option>
          </select>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <input v-model="filters.startDate" type="date" class="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            @change="applyFilters" />
          <input v-model="filters.endDate" type="date" class="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            @change="applyFilters" />
        </div>
      </div>
    </div>



    <!-- 内容区域 - 设为flex-1以占据剩余空间 -->
    <div class="flex-1 overflow-y-auto px-1 pb-8">


      <!-- 销售单列表 -->

      <div v-if="loading && !refreshing" class="text-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto"></div>
        <p class="text-gray-500 mt-2">加载中...</p>
      </div>

      <div v-else-if="sales.length === 0" class="text-center py-8">
        <svg class="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
        <p class="text-gray-500">暂无销售记录</p>
        <button @click="$router.push('/mobile/sales/add')"
          class="mt-4 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors">
          新增销售单
        </button>
      </div>

      <div v-else class="space-y-1 pb-20">
        <div v-for="sale in sales" :key="sale.id"
          class="bg-white rounded-lg p-2 shadow-sm border border-gray-100 hover:shadow-md transition-shadow mx-1 relative"
          @click="viewSaleDetail(sale)">
          
          <!-- 选中遮罩/边框 -->
          <div v-if="selectedRows.includes(sale.id)" class="absolute inset-0 border-2 border-green-500 rounded-lg pointer-events-none z-10"></div>
          
          <div class="flex items-start">
            <!-- 选择框 -->
            <div class="mr-2 pt-1" @click.stop>
              <input type="checkbox" 
                     :checked="selectedRows.includes(sale.id)"
                     @change="toggleRowSelection(sale.id)"
                     class="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500">
            </div>
            
            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between mb-1">
                <div>
                  <h4 class="font-semibold text-gray-900 text-sm">
                    {{ sale.orderNo }}
                    <span v-if="sale.deliveryNo" class="text-xs text-gray-500 bg-gray-100 px-1 rounded">
                      {{ sale.deliveryNo }}
                    </span>
                    <span v-if="sale.status === 1"
                      class="px-1 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">退货</span>
                  </h4>
                  <p class="text-xs text-gray-500 mt-0.5">{{ sale.customerName }}</p>
                </div>
              </div>

              <div class="flex items-center flex-wrap gap-1 mb-2">
                <span :class="getSalesmanAuditStatusClass(sale.salesmanAuditStatus)">
                  {{ sale.salesmanAuditStatus }}
                </span>
                <span :class="getAuditStatusClass(sale.auditStatus)">
                  {{ sale.auditStatus }}
                </span>
                <span :class="getPaymentStatusClass(sale.paymentStatus)">
                  {{ sale.paymentStatus }}
                </span>
                <span :class="getInvoiceStatusClass(sale.invoiceStatus)">
                  {{ sale.invoiceStatus }}
                </span>
              </div>

             
  
              <div class="grid grid-cols-2 gap-2 text-xs mb-2">
                <div class="flex items-center space-x-2">
                  <span class="text-gray-500">销售金额：</span>
                  <span class="font-semibold text-gray-900">¥{{ parseFloat(sale.totalAmount || 0).toLocaleString() }}</span>
                </div>
                <div class="flex items-center space-x-2">
                  <span class="text-gray-500">销售日期：</span>
                  <span class="font-semibold text-gray-900">{{ formatDate(sale.orderDate) }}</span>
                </div> 
              </div>
  
              <!-- 商品信息 -->
              <div class="mb-2">
                <p class="text-xs text-gray-500 mb-0.5">商品明细:</p>
                <p class="text-xs text-gray-700 line-clamp-1" :title="sale.productDetails">{{ sale.productDetails }}</p>
              </div>
  
              <div class="flex items-center justify-between pt-2 border-t border-gray-100">
                <div class="text-xs text-gray-500">
                  <span>业务员：{{ sale.salesmanName || '-' }}</span>
                  <span class="mx-1">|</span>
                  <span>制单人：{{ sale.creatorName || '-' }}</span>
                </div>
                <div class="text-right ml-2 flex-shrink-0 space-x-1 flex items-center">
                  <!-- 打印按钮 -->
                  <button @click.stop="handlePrint(sale, $event)"
                    class="p-1 text-gray-400 hover:text-blue-500">
                    <i class="fas fa-print"></i>
                  </button>

                  <button
                    v-if="canInvoice(sale)"
                    @click.stop="handleInvoice(sale)"
                    class="px-2 py-0.5 bg-purple-500 text-white text-xs rounded hover:bg-purple-600 transition-colors"
                  >
                    开票
                  </button>

                  <button v-if="sale.salesmanAuditStatus === '未审核'" @click.stop="handleSalesmanAudit(sale)"
                    class="px-2 py-0.5 bg-indigo-500 text-white text-xs rounded hover:bg-indigo-600 transition-colors">
                    业务员审核
                  </button>
                  <button v-if="sale.auditStatus === '未审核' && sale.salesmanAuditStatus === '已审核'" @click.stop="handleAudit(sale)"
                    class="px-2 py-0.5 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors">
                    仓库审核
                  </button>
                  <button v-if="sale.auditStatus === '未审核' && sale.salesmanAuditStatus === '已审核'" @click.stop="handleSalesmanUnAudit(sale)"
                    class="px-2 py-0.5 bg-indigo-400 text-white text-xs rounded hover:bg-indigo-500 transition-colors">
                    业务员反审
                  </button>
                  <button v-if="sale.auditStatus === '未审核'" @click.stop="handleDelete(sale)"
                    class="px-2 py-0.5 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors">
                    删除
                  </button>
                  <button v-if="(sale.paymentStatus === '未收款' || sale.paymentStatus === '部分收款') && sale.auditStatus === '已审核'"
                    @click.stop="handleUnAudit(sale)"
                    class="px-2 py-0.5 bg-yellow-500 text-white text-xs rounded hover:bg-yellow-600 transition-colors">
                    仓库反审
                  </button>
                  <button v-if="sale.paymentStatus !== '已收款' && sale.auditStatus === '已审核'"
                    @click.stop="handlePayment(sale)"
                    class="px-2 py-0.5 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors">
                    收款
                  </button>
                  <!-- <button @click.stop="viewSaleDetail(sale)"
                    class="px-2 py-0.5 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition-colors">
                    详情
                  </button> -->
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 批量操作栏 -->
    <div v-if="selectedRows.length > 0" 
         class="fixed bottom-16 left-0 right-0 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.1)] z-30 px-4 py-3 flex items-center justify-between border-t border-gray-100">
      <div class="flex items-center space-x-2">
        <label class="flex items-center space-x-2 text-sm text-gray-600">
          <input type="checkbox" 
                 :checked="isAllSelected"
                 @change="handleSelectAll"
                 class="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500">
          <span>全选 ({{ selectedRows.length }})</span>
        </label>
      </div>
      <div class="flex items-center space-x-3">
        <button @click="handleBatchInvoice"
          class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg shadow-sm hover:bg-blue-700 active:bg-blue-800 transition-colors">
          批量开票
        </button>
        <button @click="handleBatchReceipt"
          :disabled="!canBatchReceipt"
          :class="canBatchReceipt ? 'bg-green-600 hover:bg-green-700 active:bg-green-800' : 'bg-gray-300 cursor-not-allowed'"
          class="px-4 py-2 text-white text-sm font-medium rounded-lg shadow-sm transition-colors">
          批量收款
        </button>
      </div>
    </div>

    <!-- 移动端分页组件 - 固定在底部 -->
    <div v-if="!loading && sales.length > 0 && selectedRows.length === 0" class="fixed bottom-16 left-0 right-0 bg-white border-gray-200 shadow-lg z-40 px-2 py-1">
      <MobilePager 
        :current-page="pagination.currentPage"
        :total-pages="Math.ceil(pagination.total / pagination.pageSize)"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        :show-size-changer="false"
        :show-quick-jumper="false"
        @update:current-page="handlePageChange"
      />
    </div>

    <!-- 下拉刷新提示 -->
    <!-- <div v-if="refreshing"
      class="fixed top-16 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-4 py-2 rounded-lg text-sm z-50">
      正在刷新...
    </div> -->
    
    <!-- 审核确认对话框 -->
    <ConfirmDialog
      :visible="auditConfirmDialogVisible"
      type="audit"
      title="仓库审核确认"
      :message="`确定要审核销售单吗？`"
      :item-name="pendingAuditItem?.orderNo"
      :loading="auditLoading"
      @confirm="confirmAuditAction"
      @cancel="auditConfirmDialogVisible = false"
    />

    <!-- 业务员审核确认对话框 -->
    <ConfirmDialog
      :visible="salesmanAuditConfirmDialogVisible"
      type="audit"
      title="业务员审核确认"
      :message="`确定要对该销售单进行业务员审核吗？`"
      :item-name="pendingSalesmanAuditItem?.orderNo"
      :loading="salesmanAuditLoading"
      @confirm="confirmSalesmanAuditAction"
      @cancel="salesmanAuditConfirmDialogVisible = false"
    />

    <!-- 仓库反审核确认对话框 -->
    <ConfirmDialog
      :visible="unauditConfirmDialogVisible"
      type="unaudit"
      title="仓库反审核确认"
      :message="`确定要对该销售单进行仓库反审核吗？`"
      :item-name="pendingUnAuditItem?.orderNo"
      :loading="unauditLoading"
      @confirm="confirmUnAuditAction"
      @cancel="unauditConfirmDialogVisible = false"
    />

    <!-- 业务员反审核确认对话框 -->
    <ConfirmDialog
      :visible="salesmanUnauditConfirmDialogVisible"
      type="unaudit"
      title="业务员反审核确认"
      :message="`确定要对该销售单进行业务员反审核吗？`"
      :item-name="pendingSalesmanUnAuditItem?.orderNo"
      :loading="salesmanUnauditLoading"
      @confirm="confirmSalesmanUnAuditAction"
      @cancel="salesmanUnauditConfirmDialogVisible = false"
    />

    <!-- 删除确认对话框 -->
    <ConfirmDialog
      :visible="deleteConfirmDialogVisible"
      type="delete"
      title="删除确认"
      message="确定要删除该销售单吗？"
      :item-name="pendingDeleteItem?.orderNo"
      warning-text="删除后将无法恢复，请谨慎操作！"
      :loading="deleteLoading"
      @confirm="confirmDeleteAction"
      @cancel="deleteConfirmDialogVisible = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { salesApi } from '@/api/api'
import dayjs from 'dayjs'
import { toast } from '@/utils/toast.js'
import MobilePager from '@/mobile/components/MobilePager.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'

const router = useRouter()

// 响应式数据
const loading = ref(false)
const refreshing = ref(false)
const searching = ref(false)
const showFilter = ref(false)
const searchKeyword = ref('')
const showQuickActions = ref(false)
const selectedRows = ref([])

const isAllSelected = computed(() => {
  return sales.value.length > 0 && selectedRows.value.length === sales.value.length
})

const handleSelectAll = () => {
  if (isAllSelected.value) {
    selectedRows.value = []
  } else {
    selectedRows.value = sales.value.map(item => item.id)
  }
}

const toggleRowSelection = (id) => {
  const index = selectedRows.value.indexOf(id)
  if (index === -1) {
    selectedRows.value.push(id)
  } else {
    selectedRows.value.splice(index, 1)
  }
}

// 批量操作权限判断
const canBatchReceipt = computed(() => {
  if (selectedRows.value.length === 0) return false
  const selectedItems = sales.value.filter(item => selectedRows.value.includes(item.id))
  return selectedItems.every(item => 
    item.auditStatus === '已审核' && 
    ['未收款', '部分收款', '部分核销'].includes(item.paymentStatus)
  )
})

const handleBatchInvoice = () => {
  if (selectedRows.value.length === 0) return
  
  const selectedItems = sales.value.filter(item => selectedRows.value.includes(item.id))
  const validItems = selectedItems.filter(row =>
    ['未开票', '部分开票', '无需发票'].includes(row.invoiceStatus) && row.auditStatus === '已审核'
  )

  if (validItems.length === 0) {
    toast.warning('所选单据中没有已审核且未开票状态的单据')
    return
  }

  // 检查是否为同一客户的订单
  const customerNames = [...new Set(validItems.map(item => item.customerName))];
  if (customerNames.length > 1) {
    toast.warning('批量开票只能选择同一客户的订单');
    return;
  }

  const selectedIds = validItems.map(item => item.id)
  const customerId = validItems[0].customerId
  const customerName = validItems[0].customerName

  router.push({
    path: '/mobile/finance/invoice/add',
    query: {
      customerId,
      customerName,
      orderIds: selectedIds.join(',')
    }
  })
}

const handleBatchReceipt = () => {
  if (selectedRows.value.length === 0) {
    toast.warning('请先选择需要收款的销售单据')
    return
  }

  const selectedItems = sales.value.filter(item => selectedRows.value.includes(item.id))
  const validItems = selectedItems.filter(row =>
    row.auditStatus === '已审核' &&
    ['未收款', '部分核销', '部分收款'].includes(row.paymentStatus)
  )

  if (validItems.length === 0) {
    toast.warning('所选单据中没有已审核且有未收金额的单据，请重新选择')
    return
  }

  const customerNames = [...new Set(validItems.map(item => item.customerName))]
  if (customerNames.length > 1) {
    toast.warning('批量收款只能选择同一客户的订单，请重新选择')
    return
  }

  const customerId = validItems[0].customerId
  const customerName = validItems[0].customerName
  const orderIds = validItems.map(item => item.id).join(',')

  router.push({
    path: '/mobile/finance/receipt/add',
    query: {
      customerId,
      customerName,
      orderIds
    }
  })
}

const canInvoice = (sale) => {
  return sale.auditStatus === '已审核' && ['未开票', '部分开票'].includes(sale.invoiceStatus)
}

const handleInvoice = (sale) => {
  if (!canInvoice(sale)) {
    toast.warning('只能对已审核且未开票或部分开票的销售单申请开票')
    return
  }

  router.push({
    path: '/mobile/finance/invoice/add',
    query: {
      customerId: sale.customerId,
      customerName: sale.customerName,
      orderIds: String(sale.id)
    }
  })
}

const handlePrint = (item, event) => {
  event?.stopPropagation()
  router.push({
    name: 'SalesPrint',
    params: { id: item.id }
  })
}

const getInvoiceStatusClass = (status) => {
  const baseClass = 'px-2 py-0.5 text-xs rounded-full font-medium'
  switch (status) {
    case '已开票': return `${baseClass} bg-green-100 text-green-700`
    case '未开票': return `${baseClass} bg-red-100 text-red-700`
    default: return `${baseClass} bg-blue-100 text-blue-700`
  }
}


// 销售数据
const sales = ref([])
const summaryData = ref({
  totalAmount: 0,
  refundAmount: 0,
  unrefundAmount: 0,
  orderCount: 0,
  unAuditOrder: 0
})

// 分页参数
const pagination = ref({
  currentPage: 1,
  pageSize: 10,
  total: 0
})

// 搜索和筛选参数
const searchParams = ref({
  ProductName: '',
  OrderNo: '',
  CustomerName: '',
  SalesmanName: '',
  CreatorName: '',
  InvoiceStatus: null,
  PaymentStatus: [],
  AuditStatus: null,
  SalesmanAuditStatus: null,
  StartDate: null,
  EndDate: null
})

const filters = ref({
  auditStatus: null,
  salesmanAuditStatus: null,
  paymentStatus: '',
  startDate: '',
  endDate: ''
})

// 状态映射
const paymentStatusMap = {
  0: '未收款',
  1: '部分收款',
  2: '已收款',
  3: '已核销',
  4: '部分核销',
  5: '已退款',
  6: '待核销'
}

const auditStatusMap = {
  0: '未审核',
  1: '已审核'
}

const salesmanAuditStatusMap = {
  0: '未审核',
  1: '已审核'
}

const invoiceStatusMap = {
  0: '未开票',
  1: '部分开票',
  2: '已开票',
  3: '已作废',
  4: '无需发票',
  5: '待开票'
}

// 防抖搜索
let searchTimeout = null
const debounceSearch = () => {
  clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
      handleSearch()
    })
  }

// 获取业务员审核状态样式
const getSalesmanAuditStatusClass = (status) => {
  const baseClass = 'px-2 py-0.5 rounded-full text-xs font-medium'
  const classes = {
    '未审核': 'bg-red-100 text-red-800',
    '已审核': 'bg-green-100 text-green-800'
  }
  return `${baseClass} ${classes[status] || 'bg-gray-100 text-gray-800'}`
}

// 获取审核状态样式
const getAuditStatusClass = (status) => {
  const baseClass = 'px-2 py-0.5 rounded-full text-xs font-medium'
  const classes = {
    '未审核': 'bg-red-100 text-red-800',
    '已审核': 'bg-green-100 text-green-800'
  }
  return `${baseClass} ${classes[status] || 'bg-gray-100 text-gray-800'}`
}

// 获取收款状态样式  
const getPaymentStatusClass = (status) => {
  const baseClass = 'px-2 py-0.5 rounded-full text-xs font-medium'
  const classes = {
    '未收款': 'bg-red-100 text-red-700',
    '部分收款': 'bg-orange-100 text-orange-800',
    '已收款': 'bg-green-100 text-green-800',
    '已核销': 'bg-blue-100 text-blue-800',
    '部分核销': 'bg-purple-100 text-purple-800',
    '已退款': 'bg-gray-100 text-gray-800',
    '待核销': 'bg-indigo-100 text-indigo-800'
  }
  return `${baseClass} ${classes[status] || 'bg-gray-100 text-gray-800'}`
}

// 格式化日期
const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  return dayjs(dateStr).format('YYYY/MM/DD')
}

// 搜索处理
const handleSearch = async () => {
  searching.value = true
  pagination.value.currentPage = 1
  selectedRows.value = []
  await fetchData(true)
  searching.value = false
}

const handleUnauditedStatClick = () => {
  const isUnaudited = filters.value.auditStatus === 0
  filters.value.auditStatus = isUnaudited ? null : 0
  filters.value.salesmanAuditStatus = null
  showFilter.value = true
  applyFilters()
}

// 应用筛选
const applyFilters = () => {
  // 将筛选条件映射到搜索参数
  searchParams.value.AuditStatus = filters.value.auditStatus
  searchParams.value.SalesmanAuditStatus = filters.value.salesmanAuditStatus
  if (filters.value.paymentStatus) {
    searchParams.value.PaymentStatus = [parseInt(filters.value.paymentStatus)]
  } else {
    searchParams.value.PaymentStatus = []
  }
  searchParams.value.StartDate = filters.value.startDate || null
  searchParams.value.EndDate = filters.value.endDate || null

  handleSearch()
}

// 获取销售数据
const fetchData = async (reset = false) => {
  try {
    if (reset) {
      loading.value = true
      sales.value = []
      pagination.value.currentPage = 1
    }

    // 构建搜索参数
    const keyword = searchKeyword.value.trim()
    
    // 智能处理开始日期逻辑
    const getSmartStartDate = () => {
      // 如果用户明确设置了开始日期，使用用户设置的日期
      if (searchParams.value.StartDate) {
        return searchParams.value.StartDate
      }
      
      // 检查是否有其他查询条件
      const hasOtherConditions = keyword || 
        searchParams.value.OrderNo || 
        searchParams.value.CustomerName || 
        searchParams.value.SalesmanName || 
        searchParams.value.CreatorName || 
        searchParams.value.PaymentStatus?.length > 0 || 
        searchParams.value.AuditStatus !== null || 
        searchParams.value.SalesmanAuditStatus !== null ||
        searchParams.value.EndDate
      
      // 如果没有任何查询条件，默认查询本月数据
      if (!hasOtherConditions) {
        const currentMonth = new Date()
        return currentMonth.getFullYear() + '-' + String(currentMonth.getMonth() + 1).padStart(2, '0') + '-01'
      }
      
      // 如果有其他查询条件但未指定开始日期，返回null（不限制开始日期）
      return null
    }
    
    const searchData = {
      ...searchParams.value,
      ProductName: keyword,
      StartDate: getSmartStartDate()
    }
    
    const params = {
      pagination: {
        current: pagination.value.currentPage,
        pageSize: pagination.value.pageSize
      },
      search: searchData,
      sort: {
        predicate: "orderDate",
        reverse: true
      }
    }

    const response = await salesApi.getSalesList(params)

    if (response.success) {
      const newSales = response.data.list.map(item => ({
        ...item,
        paymentStatus: paymentStatusMap[item.paymentStatus],
        auditStatus: auditStatusMap[item.auditStatus],
        salesmanAuditStatus: salesmanAuditStatusMap[item.salesmanAuditStatus || 0],
        invoiceStatus: invoiceStatusMap[item.invoiceStatus]
      }))

      sales.value = newSales

      pagination.value = {
        total: response.data.pagination.total,
        currentPage: response.data.pagination.current,
        pageSize: response.data.pagination.pageSize
      }

      // 更新统计数据
      if (response.data.summary) {
        summaryData.value = {
          totalAmount: response.data.summary.totalAmount || 0,
          refundAmount: response.data.summary.receivedAmount || 0,
          unrefundAmount: response.data.summary.unreceivedAmount || 0,
          orderCount: response.data.summary.orderCount || 0,
          unAuditOrder: response.data.summary.unAuditOrder || 0
        }
      }
    }
  } catch (error) {
    console.error('获取销售数据失败:', error)
    // 简化错误处理，移动端不显示复杂的错误提示
  } finally {
    loading.value = false
    refreshing.value = false
  }
}

// 页面变化处理
const handlePageChange = async (page) => {
  // 立即设置loading状态并清空当前数据
  loading.value = true
  sales.value = []
  pagination.value.currentPage = page
  // 获取新数据
  await fetchData()
}

// 刷新数据
const refreshData = async () => {
  refreshing.value = true
  await fetchData(true)
}

// 查看销售详情
const viewSaleDetail = (sale) => {
  router.push(`/mobile/sales/detail/${sale.id}`)
}

// 确认对话框相关状态
const auditConfirmDialogVisible = ref(false)
const pendingAuditItem = ref(null)
const auditLoading = ref(false)

const salesmanAuditConfirmDialogVisible = ref(false)
const pendingSalesmanAuditItem = ref(null)
const salesmanAuditLoading = ref(false)

// 业务员审核销售单
const handleSalesmanAudit = (sale) => {
  pendingSalesmanAuditItem.value = sale
  salesmanAuditConfirmDialogVisible.value = true
}

const confirmSalesmanAuditAction = async () => {
  if (!pendingSalesmanAuditItem.value) return
  
  try {
    salesmanAuditLoading.value = true
    const sale = pendingSalesmanAuditItem.value
    
    const res = await salesApi.AuditOrderBySalesman(parseInt(sale.id))
    if (res.success) {
      sale.salesmanAuditStatus = '已审核'
      salesmanAuditConfirmDialogVisible.value = false
      toast.success(`销售单 ${sale.orderNo} 业务员审核成功`)
      await fetchData()
    } else {
      const errMsg = res.message || '未知错误'
      toast.error(`业务员审核失败：${errMsg}`)
    }
  } catch (error) {
    console.error('业务员审核失败:', error)
    const errMsg = error?.response?.data?.message || error?.message || error || '网络错误，请稍后重试'
    toast.error(`业务员审核失败：${errMsg}`)
  } finally {
    salesmanAuditLoading.value = false
    pendingSalesmanAuditItem.value = null
  }
}

// 仓库审核销售单
const handleAudit = (sale) => {
  pendingAuditItem.value = sale
  auditConfirmDialogVisible.value = true
}

const confirmAuditAction = async () => {
  if (!pendingAuditItem.value) return
  
  try {
    auditLoading.value = true
    const sale = pendingAuditItem.value
    
    const res = await salesApi.AuditOrder(parseInt(sale.id))
    if (res.success) {
      sale.auditStatus = '已审核'
      auditConfirmDialogVisible.value = false
      toast.success(`销售单 ${sale.orderNo} 仓库审核成功`)
      await fetchData()
    } else {
      const errMsg = res.message || '未知错误'
      toast.error(`仓库审核失败：${errMsg}`)
    }
  } catch (error) {
    console.error('仓库审核失败:', error)
    const errMsg = error?.response?.data?.message || error?.message || error || '网络错误，请稍后重试'
    toast.error(`仓库审核失败：${errMsg}`)
  } finally {
    auditLoading.value = false
    pendingAuditItem.value = null
  }
}

// 收款处理
const handlePayment = (sale) => {
  if (sale.auditStatus !== '已审核') {
    toast.warning('只能对已审核的订单进行收款')
    return
  }
  if (parseFloat(sale.unreceivedAmount) <= 0) {
    toast.warning('该订单没有未收金额')
    return
  }
  router.push({
    path: '/mobile/finance/receipt/add',
    query: {
      customerId: sale.customerId,
      customerName: sale.customerName,
      orderIds: String(sale.id)
    }
  })
}

const unauditConfirmDialogVisible = ref(false)
const pendingUnAuditItem = ref(null)
const unauditLoading = ref(false)

const handleUnAudit = (sale) => {
  pendingUnAuditItem.value = sale
  unauditConfirmDialogVisible.value = true
}

const confirmUnAuditAction = async () => {
  if (!pendingUnAuditItem.value) return
  
  try {
    unauditLoading.value = true
    const sale = pendingUnAuditItem.value
    
    const res = await salesApi.UnAuditOrder(parseInt(sale.id))
    if (res.success) {
      sale.auditStatus = '未审核'
      unauditConfirmDialogVisible.value = false
      toast.success(`销售单 ${sale.orderNo} 仓库反审核成功`)
      await fetchData()
    } else {
      toast.error(`仓库反审核失败：${res.message || '未知错误'}`)
    }
  } catch (error) {
    console.error('仓库反审核失败:', error)
    const errMsg = error?.response?.data?.message || error?.message || error || '网络错误，请稍后重试'
    toast.error(`仓库反审核失败：${errMsg}`)
  } finally {
    unauditLoading.value = false
    pendingUnAuditItem.value = null
  }
}

const salesmanUnauditConfirmDialogVisible = ref(false)
const pendingSalesmanUnAuditItem = ref(null)
const salesmanUnauditLoading = ref(false)

const handleSalesmanUnAudit = (sale) => {
  pendingSalesmanUnAuditItem.value = sale
  salesmanUnauditConfirmDialogVisible.value = true
}

const confirmSalesmanUnAuditAction = async () => {
  if (!pendingSalesmanUnAuditItem.value) return
  
  try {
    salesmanUnauditLoading.value = true
    const sale = pendingSalesmanUnAuditItem.value
    
    const res = await salesApi.UnAuditOrderBySalesman(parseInt(sale.id))
    if (res.success) {
      sale.salesmanAuditStatus = '未审核'
      salesmanUnauditConfirmDialogVisible.value = false
      toast.success(`销售单 ${sale.orderNo} 业务员反审核成功`)
      await fetchData()
    } else {
      toast.error(`业务员反审核失败：${res.message || '未知错误'}`)
    }
  } catch (error) {
    console.error('业务员反审核失败:', error)
    const errMsg = error?.response?.data?.message || error?.message || error || '网络错误，请稍后重试'
    toast.error(`业务员反审核失败：${errMsg}`)
  } finally {
    salesmanUnauditLoading.value = false
    pendingSalesmanUnAuditItem.value = null
  }
}

const deleteConfirmDialogVisible = ref(false)
const pendingDeleteItem = ref(null)
const deleteLoading = ref(false)

const handleDelete = (sale) => {
  pendingDeleteItem.value = sale
  deleteConfirmDialogVisible.value = true
}

const confirmDeleteAction = async () => {
  if (!pendingDeleteItem.value) return
  
  try {
    deleteLoading.value = true
    const sale = pendingDeleteItem.value
    
    const res = await salesApi.DeleteOrder(parseInt(sale.id))
    if (res.success) {
      deleteConfirmDialogVisible.value = false
      toast.success(`销售单 ${sale.orderNo} 删除成功`)
      await fetchData()
    } else {
      toast.error(`删除失败：${res.message || '未知错误'}`)
    }
  } catch (error) {
    console.error('删除失败:', error)
    const errMsg = error?.response?.data?.message || error?.message || error || '网络错误，请稍后重试'
    toast.error(`删除失败：${errMsg}`)
  } finally {
    deleteLoading.value = false
    pendingDeleteItem.value = null
  }
}

// 快捷操作处理
const handleQuickAction = (path) => {
  showQuickActions.value = false
  router.push(path)
}

// 下拉刷新处理
let startY = 0
let moveY = 0
let isRefreshing = false

const handleTouchStart = (e) => {
  if (window.scrollY === 0 && !isRefreshing) {
    startY = e.touches[0].clientY
  }
}

const handleTouchMove = (e) => {
  if (window.scrollY === 0 && !isRefreshing) {
    moveY = e.touches[0].clientY - startY
    if (moveY > 100) {
      e.preventDefault()
    }
  }
}

const handleTouchEnd = () => {
  if (window.scrollY === 0 && moveY > 100 && !isRefreshing) {
    isRefreshing = true
    refreshData().finally(() => {
      isRefreshing = false
      moveY = 0
    })
  }
  moveY = 0
}

// 生命周期
onMounted(async () => {
  // 页面初始化时StartDate设为空字符串，但默认查询本月数据
  searchParams.value.StartDate = ''
  
  await fetchData(true)

  // 添加下拉刷新事件监听
  document.addEventListener('touchstart', handleTouchStart, { passive: false })
  document.addEventListener('touchmove', handleTouchMove, { passive: false })
  document.addEventListener('touchend', handleTouchEnd)
})

onUnmounted(() => {
  // 清理事件监听
  document.removeEventListener('touchstart', handleTouchStart)
  document.removeEventListener('touchmove', handleTouchMove)
  document.removeEventListener('touchend', handleTouchEnd)

  // 清理定时器
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
})
</script>

<style scoped>
/* 支付进度条样式 */
.bg-green-500 {
  transition: width 0.3s ease-in-out;
}

/* 确保卡片悬停效果 */
.hover\:shadow-md:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* 文本截断样式 */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 加载动画 */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>